import React from 'react';
    import { motion } from 'framer-motion';
    import SafeIcon from '../common/SafeIcon';
    import { FiShield, FiFileText, FiClock } from 'react-icons/fi';

    const TermsOfService = () => {
      return (
        <div className="bg-[#050505] min-h-screen pt-44 pb-32 px-6 grid-pattern">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header Section */}
            <header className="mb-20 border-b border-white/5 pb-12">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#CCFF00] flex items-center justify-center text-black">
                  <SafeIcon icon={FiShield} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-[#CCFF00] font-mono-data text-[10px] font-black uppercase tracking-[0.4em]">Legal Protocol</h2>
                  <p className="text-white font-black uppercase tracking-tighter">Edition v4.0</p>
                </div>
              </div>
              
              <h1 className="text-6xl md:text-8xl font-black text-white leading-[0.85] tracking-tighter mb-8 uppercase">
                Terms of <br />
                <span className="text-transparent" style={{ WebkitTextStroke: '1px #CCFF00' }}>Service</span>
              </h1>

              <div className="flex items-center gap-6 text-slate-500 font-mono-data text-[10px] uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="text-[#CCFF00]" />
                  Last Updated: 5th of May 2026
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiFileText} className="text-[#CCFF00]" />
                  Ref: B2B-TOS-2026
                </div>
              </div>
            </header>

            {/* Content Section */}
            <div className="space-y-16">
              <section>
                <p className="text-xl text-slate-300 font-medium leading-relaxed italic mb-12">
                  Welcome to The Pitch Feed ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our football news feed API and related services (the "Service"). By subscribing to or using the Service, you ("Client," "you," or "your") agree to be bound by these Terms.
                </p>
              </section>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">01. Service Description</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium">
                  The Pitch Feed provides a business-to-business (B2B) subscription service that delivers football news, updates, and related content via an API integration.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">02. License & Usage</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium space-y-4">
                  <p><b>2.1. Grant of License:</b> Subject to your continuous compliance with these Terms and payment of applicable fees, we grant you a non-exclusive, non-transferable, revocable license to access, ingest, edit, adapt, and publish the content provided through the Service on your designated digital platforms.</p>
                  <p><b>2.2. Editorial Control:</b> You retain full editorial control over the content once delivered. You acknowledge that you are solely responsible for reviewing, fact-checking, and ensuring the legality of all content before publication.</p>
                  <p><b>2.3. Restrictions:</b> You may not resell, sub-license, or distribute the raw API access or the raw feed itself to any third-party news agencies or aggregators without our prior written consent.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">03. Subscription</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium space-y-4">
                  <p><b>3.1. Fees:</b> The Service is billed on a monthly subscription basis. The rate is €999 per month per language market added to your feed.</p>
                  <p><b>3.2. Billing:</b> Subscriptions are billed in advance on a monthly basis. All fees are non-refundable. We reserve the right to modify our pricing with 30 days' notice.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12 bg-[#CCFF00]/5 p-8 -mx-8 border-x-0">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">04. Warranties</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium space-y-4">
                  <p><b>4.1. "As Is":</b> The Service and all content are provided strictly on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind.</p>
                  <p><b>4.2. Accuracy:</b> While we make every maximum effort to ensure the highest quality, accuracy, and timeliness, errors or factual inaccuracies may occasionally occur. The Client acknowledges that we are completely free of any responsibility or liability for any such errors.</p>
                  <p><b>4.3. No Copyright Warranty:</b> We do not warrant that the delivery or your subsequent use of the text, headlines, or data will not infringe upon the intellectual property rights of third parties. Specifically, images are not provided due to licensing restrictions.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">05. Liability</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium space-y-4">
                  <p>To the maximum extent permitted by applicable law, in no event shall The Pitch Feed, its owners, directors, or employees be liable for any direct, indirect, incidental, or special damages, including loss of profits or data.</p>
                  <p>Our total cumulative liability to you for any and all claims shall under no circumstances exceed the total amount paid by you to us in the one (1) month immediately preceding the event.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">06. Indemnification</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium">
                  You agree to defend, indemnify, and hold harmless The Pitch Feed from and against any and all claims, damages, and expenses arising from your use of the Service or your publication of any content obtained from the Service.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-12">
                <div className="md:col-span-4">
                  <h3 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#CCFF00] mb-4">09. Governing Law</h3>
                </div>
                <div className="md:col-span-8 text-slate-400 leading-relaxed font-medium">
                  These Terms shall be governed and construed in accordance with the laws of Denmark. Any dispute shall be subject to the exclusive jurisdiction of the courts of Denmark.
                </div>
              </div>

              <div className="pt-20 border-t border-white/10 text-center">
                <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.5em] mb-4">End of Document</p>
                <div className="h-1 w-20 bg-[#CCFF00] mx-auto"></div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    };

    export default TermsOfService;